import { all, call, put, select, takeLatest, takeEvery } from "redux-saga/effects";
import { GlobalEntityType } from "shared/types";
import BackendClient from "api/backend-client";
import { RecipeScalingApiMethods } from "api/api-methods";
import { isNotEmpty } from "utils/validate";
import { waitForLogin } from "../auth/auth.saga";
import { addRecipeCustomVariableFailure, addRecipeCustomVariableSuccessful, addRecipeStepFailure, addRecipeStepSuccessful, addWrapStepFailure, addWrapStepSuccessful, alternativeStepChangeDefaultFlowFailure, alternativeStepChangeDefaultFlowSuccessful, alternativeStepSetActiveFlow, complexStepAddFlowFailure, complexStepAddFlowSuccessful, complexStepDeleteFlowFailure, complexStepDeleteFlowSuccessful, complexStepMoveFlowFailure, complexStepMoveFlowSuccessful, concurrentStepSetFlowMustCompeteFailure, concurrentStepSetFlowMustCompeteSuccessful, conditionalStepUpdateFlowFailure, conditionalStepUpdateFlowSuccessful, deleteGasFailure, deleteGasSuccessful, deleteLiquidFailure, deleteLiquidSuccessful, deleteParameterFailure, deleteParameterSuccessful, deleteRecipeCustomVariableFailure, deleteRecipeCustomVariableSuccessful, editRecipeCustomVariableFailure, editRecipeCustomVariableSuccessful, getCustomVariableTransformationsFailure, getCustomVariableTransformationsSuccessful, getRecipeCustomVariableDefaultNameFailure, getRecipeCustomVariableDefaultNameSuccessful, getRecipeCustomVariableFormulaStatusFailure, getRecipeCustomVariableFormulaStatusSuccessful, getRecipeStepsListFailure, getRecipeStepsListSuccessful, loadAlternativeStepNamesFailure, loadAlternativeStepNamesSuccessful, loadGasNamesFailure, loadGasNamesSuccessful, loadLiquidNamesFailure, loadLiquidNamesSuccessful, loadParameterNamesFailure, loadParameterNamesSuccessful, loadRecipeFailure, loadRecipeSuccessful, loopStepUpdateConditionFailure, loopStepUpdateConditionSuccessful, moveRecipeStepsFailure, moveRecipeStepsSuccessful, RecipeActionType, removeAlternativeStepFailure, removeAlternativeStepSuccessful, removeRecipeStepsFailure, removeRecipeStepsSuccessful, setPlusIconPlacement, setRecipeDialogStates, setRecipeDialogStatesFailure, setRecipeDialogStatesSuccess, updateAlternativeStepFailure, updateAlternativeStepSuccessful, updateStepFailure, updateStepSuccessful, upsertGasFailure, upsertGasSuccessful, upsertLiquidFailure, upsertLiquidSuccessful, upsertParameterFailure, upsertParameterSuccessful, } from "./recipe.actions";
import { RecipeDialogType, RecipeStepType, } from "./recipe.types";
import { RequestTemplate } from "../knowledge-base-item/knowledge-base-item.types";
import { connectChannelSaga, loadItemErrorHandler } from "../knowledge-base-item/knowledge-base-item.saga";
import { makeUnsaved, setCurrentRequestTemplate, setDeleteTarget, setServerError, setupKnowledgeBaseItem, } from "../knowledge-base-item/knowledge-base-item.actions";
import { apiErrorHandler, checkConnectionSaga, loadProcessVariablesSaga, loadRecipeParameterTypesSaga, } from "store/global/global.saga";
import { alternativeStepActiveFlowsSelector, plusIconPlacementSelector, recipeStepsSelector, } from "./recipe.selectors";
import { checkIsComplexStep, defineSetAlternativeActiveFlowsPayloadOnOpenStep, getStepTrunks, } from "./recipe.utils";
import { toggleNoSeatsToastVisible } from "store/licensing/licensing.actions";
import { noSeatsAvailableSelector } from "store/licensing/licensing.selectors";
import { isEditableSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
function* getRecipeStepsListSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const list = yield call([
            backendClient,
            RecipeScalingApiMethods.GetRecipeStepsList,
        ]);
        yield put(getRecipeStepsListSuccessful(list));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "recipe steps list load failed");
        yield put(getRecipeStepsListFailure());
    }
}
function* loadRecipeSaga({ payload }) {
    try {
        yield call(checkConnectionSaga);
        yield call(waitForLogin);
        yield all([
            call(getRecipeStepsListSaga),
            call(loadRecipeParameterTypesSaga),
            call(loadProcessVariablesSaga),
            call(getCustomVariableTransformationsSaga),
        ]);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.GetRecipe], payload);
        const { name, description, lastSavedDate, createdAt, createdBy, lastSavedBy, tags, state, isCompleted, hasUnsavedChanges, isLocked, owners, } = data;
        yield put(loadRecipeSuccessful({ state }));
        yield put(setupKnowledgeBaseItem({
            id: payload,
            name,
            description,
            lastSavedDate,
            createdAt,
            createdBy,
            lastSavedBy,
            tags,
            hasUnsavedChanges,
            entityType: GlobalEntityType.Recipe,
            isLocked,
            isCompleted,
            owners,
        }));
        const noSeatsAvailable = yield select(noSeatsAvailableSelector);
        if (noSeatsAvailable) {
            const editable = yield select(isEditableSelector);
            if (editable) {
                yield put(toggleNoSeatsToastVisible(true));
            }
        }
        else {
            yield call(connectChannelSaga);
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "recipe load failed");
        yield call(loadItemErrorHandler, e);
        yield put(loadRecipeFailure());
    }
}
export function* recovery(id) {
    yield call(waitForLogin);
    const backendClient = BackendClient.getInstance();
    const state = yield call([backendClient, RecipeScalingApiMethods.GetRecipeTemporary], id);
    yield put(loadRecipeSuccessful({ state: state || null }));
}
function* addRecipeStepSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        const plusIconPlacement = yield select(plusIconPlacementSelector);
        const newPlusIconPlacement = {
            previousStepId: undefined,
            complexStepAttrs: plusIconPlacement.complexStepAttrs,
        };
        const previousStepId = plusIconPlacement.previousStepId ?? -1;
        const baseComplexStepPayload = {
            previousStepId,
            stepId: plusIconPlacement.complexStepAttrs?.stepId,
            flowId: plusIconPlacement.complexStepAttrs?.trunkId,
        };
        let data;
        const addToComplexStepPayload = { ...baseComplexStepPayload, ...payload };
        switch (plusIconPlacement.complexStepAttrs?.type) {
            case RecipeStepType.CONCURRENT:
                data = yield call([backendClient, RecipeScalingApiMethods.AddRecipeStepToConcurrent], addToComplexStepPayload);
                break;
            case RecipeStepType.CONDITIONAL:
                data = yield call([backendClient, RecipeScalingApiMethods.AddRecipeStepToConditional], addToComplexStepPayload);
                break;
            case RecipeStepType.LOOP:
                data = yield call([backendClient, RecipeScalingApiMethods.AddRecipeStepToLoop], addToComplexStepPayload);
                break;
            case RecipeStepType.ALTERNATIVE:
                data = yield call([backendClient, RecipeScalingApiMethods.AddRecipeStepToAlternative], addToComplexStepPayload);
                break;
            default: {
                const addStepPayload = { ...payload, previousStepId };
                data = yield call([backendClient, RecipeScalingApiMethods.AddRecipeStep], addStepPayload);
            }
        }
        newPlusIconPlacement.previousStepId = data.step.id;
        if (checkIsComplexStep(data.step)) {
            const trunks = getStepTrunks(data.step);
            newPlusIconPlacement.previousStepId = undefined;
            newPlusIconPlacement.complexStepAttrs = {
                stepId: data.step.id,
                trunkId: trunks[0].id,
                type: data.step.type,
            };
        }
        yield put(addRecipeStepSuccessful(data.state));
        yield put(setPlusIconPlacement(newPlusIconPlacement));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "add recipe step failed");
        yield put(addRecipeStepFailure());
    }
}
function* addRecipeWrapStepSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        switch (payload.type) {
            case RecipeStepType.ALTERNATIVE:
                yield put(setCurrentRequestTemplate(RequestTemplate.ALTERNATIVE_STEP_POST));
                break;
        }
        const data = yield call([backendClient, RecipeScalingApiMethods.AddWrapRecipeStep], payload);
        const plusIconPlacement = yield select(plusIconPlacementSelector);
        const newPlusIconPlacement = { ...plusIconPlacement };
        const { step, state } = data;
        switch (step.type) {
            case RecipeStepType.ALTERNATIVE: {
                const firstTrunk = step.flowProperties[0];
                newPlusIconPlacement.previousStepId = firstTrunk?.steps[firstTrunk.steps.length - 1].id;
                newPlusIconPlacement.complexStepAttrs = {
                    type: step.type,
                    stepId: step.id,
                    trunkId: firstTrunk.id,
                };
                yield put(setRecipeDialogStates({ [RecipeDialogType.ALTERNATIVE_STEP]: { isOpen: false } }));
                break;
            }
        }
        yield put(setServerError(null));
        yield put(addWrapStepSuccessful(state));
        yield put(setPlusIconPlacement(newPlusIconPlacement));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "add recipe wrap step failed");
        yield put(addWrapStepFailure());
    }
}
function* moveRecipeStepsSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        const state = yield call([backendClient, RecipeScalingApiMethods.MoveRecipeSteps], payload);
        yield put(moveRecipeStepsSuccessful(state));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "move recipe steps failed");
        yield put(moveRecipeStepsFailure());
    }
}
function* updateStepSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        const updatedState = yield call([backendClient, RecipeScalingApiMethods.UpdateRecipeStep], payload);
        yield put(setServerError(null));
        yield put(updateStepSuccessful(updatedState));
    }
    catch (e) {
        yield call(apiErrorHandler, e, `${payload.type} update is failed`);
        yield put(updateStepFailure());
    }
}
function* complexStepAddFlowSaga({ payload }) {
    const { stepType } = payload;
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        let data;
        switch (stepType) {
            case RecipeStepType.CONDITIONAL:
                data = yield call([backendClient, RecipeScalingApiMethods.ConditionalStepAddFlow], payload);
                break;
            case RecipeStepType.CONCURRENT:
                data = yield call([backendClient, RecipeScalingApiMethods.ConcurrentStepAddFlow], payload);
                break;
            case RecipeStepType.ALTERNATIVE:
                if (!isNotEmpty(payload.duplicateFlowId)) {
                    data = yield call([backendClient, RecipeScalingApiMethods.AlternativeStepAddEmptyFlow], payload);
                }
                else {
                    data = yield call([backendClient, RecipeScalingApiMethods.AlternativeStepDuplicateFlow], payload);
                }
                yield put(alternativeStepSetActiveFlow({ payload: { [payload.stepId]: data.flow.id } }));
                break;
        }
        yield put(setServerError(null));
        yield put(complexStepAddFlowSuccessful(data.state));
        yield put(setPlusIconPlacement({
            previousStepId: data.flow.steps[data.flow.steps.length - 1]?.id,
            complexStepAttrs: {
                stepId: payload.stepId,
                trunkId: data.flow.id,
                type: stepType,
            },
        }));
    }
    catch (e) {
        yield call(apiErrorHandler, e, `${stepType} step add flow failed`);
        yield put(complexStepAddFlowFailure());
    }
}
function* complexStepDeleteFlowSaga({ payload }) {
    const { stepType } = payload.payload;
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        let updatedState;
        switch (stepType) {
            case RecipeStepType.CONCURRENT:
                updatedState = yield call([backendClient, RecipeScalingApiMethods.ConcurrentStepDeleteFlow], payload);
                break;
            case RecipeStepType.CONDITIONAL:
                updatedState = yield call([backendClient, RecipeScalingApiMethods.ConditionalStepDeleteFlow], payload);
                break;
            case RecipeStepType.ALTERNATIVE:
                updatedState = yield call([backendClient, RecipeScalingApiMethods.AlternativeStepDeleteFlow], payload);
                break;
        }
        payload.callback && payload.callback();
        yield put(setServerError(null));
        yield put(complexStepDeleteFlowSuccessful(updatedState));
        yield put(setPlusIconPlacement(payload.plusIconPlacement));
    }
    catch (e) {
        yield call(apiErrorHandler, e, `${stepType} step delete flow failed`);
        yield put(complexStepDeleteFlowFailure());
    }
}
function* complexStepMoveFlowSaga({ payload }) {
    const { stepType } = payload;
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        let updatedState;
        switch (stepType) {
            case RecipeStepType.CONCURRENT:
                updatedState = yield call([backendClient, RecipeScalingApiMethods.ConcurrentStepMoveFlow], payload);
                break;
            case RecipeStepType.CONDITIONAL:
                updatedState = yield call([backendClient, RecipeScalingApiMethods.ConditionalStepMoveFlow], payload);
                break;
        }
        yield put(setServerError(null));
        yield put(complexStepMoveFlowSuccessful(updatedState));
    }
    catch (e) {
        yield call(apiErrorHandler, e, `${stepType} step move flow failed`);
        yield put(complexStepMoveFlowFailure());
    }
}
function* concurrentStepFlowSetMustCompeteSaga({ payload, }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        const updatedState = yield call([backendClient, RecipeScalingApiMethods.ConcurrentStepSetFlowMustComplete], payload);
        yield put(setServerError(null));
        yield put(concurrentStepSetFlowMustCompeteSuccessful(updatedState));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "Concurrent step flow set must complete failed");
        yield put(concurrentStepSetFlowMustCompeteFailure());
    }
}
function* conditionalStepUpdateFlowSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        const updatedState = yield call([backendClient, RecipeScalingApiMethods.ConditionalStepUpdateFlow], payload);
        yield put(setServerError(null));
        yield put(conditionalStepUpdateFlowSuccessful(updatedState));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "Conditional step update flow failed");
        yield put(conditionalStepUpdateFlowFailure());
    }
}
function* loopStepUpdateConditionSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        const updatedState = yield call([backendClient, RecipeScalingApiMethods.LoopStepUpdateCondition], payload);
        yield put(setServerError(null));
        yield put(loopStepUpdateConditionSuccessful(updatedState));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "Loop step update condition failed");
        yield put(loopStepUpdateConditionFailure());
    }
}
function* alternativeStepChangeDefaultFlowSaga({ payload, }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        const updatedState = yield call([backendClient, RecipeScalingApiMethods.AlternativeStepChangeDefaultFlow], payload);
        yield put(setServerError(null));
        yield put(alternativeStepChangeDefaultFlowSuccessful(updatedState.state));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "group of alternative flows change default flow failed");
        yield put(alternativeStepChangeDefaultFlowFailure());
    }
}
function* updateAlternativeStepSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        yield put(setCurrentRequestTemplate(RequestTemplate.ADD_LIQUID_STEP_PUT));
        const updatedState = yield call([backendClient, RecipeScalingApiMethods.UpdateAlternativeStep], payload);
        yield put(setRecipeDialogStates({ [RecipeDialogType.ALTERNATIVE_STEP]: { isOpen: false } }));
        yield put(setServerError(null));
        yield put(updateAlternativeStepSuccessful(updatedState));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "Update group of alternative flows failed");
        yield put(updateAlternativeStepFailure());
    }
}
function* removeAlternativeStepSaga({ payload: { payload, plusIconPlacement }, }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        const updatedState = yield call([backendClient, RecipeScalingApiMethods.RemoveAlternativeStep], payload);
        yield put(setPlusIconPlacement(plusIconPlacement));
        yield put(setServerError(null));
        yield put(removeAlternativeStepSuccessful(updatedState));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "remove group of alternative flows failed");
        yield put(removeAlternativeStepFailure());
    }
}
function* removeRecipeStepsSaga({ payload: { payload, plusIconPlacement }, }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        const state = yield call([backendClient, RecipeScalingApiMethods.RemoveRecipeSteps], payload);
        yield put(removeRecipeStepsSuccessful(state));
        yield put(setPlusIconPlacement(plusIconPlacement));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "remove recipe steps failed");
        yield put(removeRecipeStepsFailure());
    }
}
function* setRecipeDialogStatesSaga({ payload }) {
    try {
        const recipeSteps = yield select(recipeStepsSelector);
        const alternativeStepActiveFlows = yield select(alternativeStepActiveFlowsSelector);
        const plusIconPlacement = yield select(plusIconPlacementSelector);
        const setAlternativeActiveFlowsPayload = defineSetAlternativeActiveFlowsPayloadOnOpenStep(payload, recipeSteps, plusIconPlacement, alternativeStepActiveFlows);
        if (setAlternativeActiveFlowsPayload) {
            yield put(alternativeStepSetActiveFlow(setAlternativeActiveFlowsPayload));
            yield put(setRecipeDialogStatesSuccess(payload));
        }
        else {
            yield put(setRecipeDialogStatesSuccess(payload));
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "set recipe dialog states failed");
        yield put(setRecipeDialogStatesFailure());
    }
}
function* loadAlternativeStepNamesSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.GetRecipeAlternativeStepNames]);
        yield put(loadAlternativeStepNamesSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "recipe load group of alternative flows names failed");
        yield put(loadAlternativeStepNamesFailure());
    }
}
function* loadGasNamesSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.GetRecipeGasNames]);
        yield put(loadGasNamesSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "recipe load gas names failed");
        yield put(loadGasNamesFailure());
    }
}
function* loadLiquidNamesSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.GetRecipeLiquidNames]);
        yield put(loadLiquidNamesSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "recipe load liquid names failed");
        yield put(loadLiquidNamesFailure());
    }
}
function* loadParameterNamesSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.GetRecipeParameterNames]);
        yield put(loadParameterNamesSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "recipe load parameter names failed");
        yield put(loadParameterNamesFailure());
    }
}
function* upsertGasSaga({ payload }) {
    const { id } = payload;
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        const currentRequestTemplate = id ? RequestTemplate.GAS_PUT : RequestTemplate.GAS_POST;
        yield put(setCurrentRequestTemplate(currentRequestTemplate));
        const method = id ? RecipeScalingApiMethods.UpdateRecipeGas : RecipeScalingApiMethods.AddRecipeGas;
        const updatedState = yield call([backendClient, method], payload);
        yield put(setServerError(null));
        yield put(upsertGasSuccessful(updatedState));
    }
    catch (e) {
        yield call(apiErrorHandler, e, `${id ? "update" : "add"} gas is failed`);
        yield put(upsertGasFailure());
    }
}
function* deleteGasSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        const currentRequestTemplate = RequestTemplate.GAS_DELETE;
        yield put(setCurrentRequestTemplate(currentRequestTemplate));
        const updatedState = yield call([backendClient, RecipeScalingApiMethods.DeleteRecipeGas], payload);
        yield put(setDeleteTarget(null));
        yield put(setServerError(null));
        yield put(deleteGasSuccessful(updatedState));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "delete gas failed");
        yield put(deleteGasFailure());
    }
}
function* upsertLiquidSaga({ payload }) {
    const { id } = payload;
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        const currentRequestTemplate = id ? RequestTemplate.LIQUID_PUT : RequestTemplate.LIQUID_POST;
        yield put(setCurrentRequestTemplate(currentRequestTemplate));
        const method = id ? RecipeScalingApiMethods.UpdateRecipeLiquid : RecipeScalingApiMethods.AddRecipeLiquid;
        const updatedState = yield call([backendClient, method], payload);
        yield put(setServerError(null));
        yield put(upsertLiquidSuccessful(updatedState));
    }
    catch (e) {
        yield call(apiErrorHandler, e, `${id ? "update" : "add"} liquid is failed`);
        yield put(upsertLiquidFailure());
    }
}
function* deleteLiquidSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        const updatedState = yield call([backendClient, RecipeScalingApiMethods.DeleteRecipeLiquid], payload);
        yield put(setDeleteTarget(null));
        yield put(setServerError(null));
        yield put(deleteLiquidSuccessful(updatedState));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "delete liquid failed");
        yield put(deleteLiquidFailure());
    }
}
function* upsertParameterSaga({ payload }) {
    const { id } = payload;
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        const currentRequestTemplate = id ? RequestTemplate.PARAMETER_PUT : RequestTemplate.PARAMETER_POST;
        yield put(setCurrentRequestTemplate(currentRequestTemplate));
        const method = id
            ? RecipeScalingApiMethods.UpdateRecipeParameter
            : RecipeScalingApiMethods.AddRecipeParameter;
        const updatedState = yield call([backendClient, method], payload);
        yield put(setServerError(null));
        yield put(upsertParameterSuccessful(updatedState));
    }
    catch (e) {
        yield call(apiErrorHandler, e, `${id ? "update" : "add"} parameter is failed`);
        yield put(upsertParameterFailure());
    }
}
function* deleteParameterSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        const updatedState = yield call([backendClient, RecipeScalingApiMethods.DeleteRecipeParameter], payload);
        yield put(setServerError(null));
        yield put(deleteParameterSuccessful(updatedState));
        yield put(setDeleteTarget(null));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "delete parameter failed");
        yield put(deleteParameterFailure());
    }
}
function* getCustomVariableTransformationsSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([
            backendClient,
            RecipeScalingApiMethods.GetRecipeCustomVariableTransformations,
        ]);
        yield put(getCustomVariableTransformationsSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "get recipe custom variable transformations failed");
        yield put(getCustomVariableTransformationsFailure());
    }
}
function* getCustomVariableDefaultNameSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const name = yield call([backendClient, RecipeScalingApiMethods.GetRecipeCustomVariableDefaultName], payload);
        yield put(getRecipeCustomVariableDefaultNameSuccessful(name));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "get recipe custom variable default name failed");
        yield put(getRecipeCustomVariableDefaultNameFailure());
    }
}
function* getCustomVariableFormulaStatusSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.GetRecipeCustomVariableFormulaStatus], payload);
        yield put(getRecipeCustomVariableFormulaStatusSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "get recipe custom variable formula status failed");
        yield put(getRecipeCustomVariableFormulaStatusFailure());
    }
}
function* addCustomVariableSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(setCurrentRequestTemplate(RequestTemplate.CUSTOM_VARIABLE_POST));
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        const state = yield call([backendClient, RecipeScalingApiMethods.AddRecipeCustomVariable], payload);
        yield put(setServerError(null));
        yield put(addRecipeCustomVariableSuccessful(state));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "add custom variable failed");
        yield put(addRecipeCustomVariableFailure());
    }
}
function* updateCustomVariableSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(setCurrentRequestTemplate(RequestTemplate.CUSTOM_VARIABLE_PUT));
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        const state = yield call([backendClient, RecipeScalingApiMethods.UpdateRecipeCustomVariable], payload);
        yield put(setServerError(null));
        yield put(editRecipeCustomVariableSuccessful(state));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "edit custom variable failed");
        yield put(editRecipeCustomVariableFailure());
    }
}
function* deleteCustomVariableSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        const state = yield call([backendClient, RecipeScalingApiMethods.DeleteRecipeCustomVariable], payload);
        yield put(setServerError(null));
        yield put(deleteRecipeCustomVariableSuccessful(state));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "delete custom variable failed");
        yield put(deleteRecipeCustomVariableFailure());
    }
}
export default function* recipeSaga() {
    yield takeLatest(RecipeActionType.LOAD, loadRecipeSaga);
    yield takeLatest(RecipeActionType.GET_STEPS_LIST, getRecipeStepsListSaga);
    yield takeLatest(RecipeActionType.ADD_STEP, addRecipeStepSaga);
    yield takeLatest(RecipeActionType.ADD_WRAP_STEP, addRecipeWrapStepSaga);
    yield takeLatest(RecipeActionType.MOVE_STEPS, moveRecipeStepsSaga);
    yield takeLatest(RecipeActionType.COMPLEX_STEP_ADD_FLOW, complexStepAddFlowSaga);
    yield takeLatest(RecipeActionType.COMPLEX_STEP_DELETE_FLOW, complexStepDeleteFlowSaga);
    yield takeLatest(RecipeActionType.COMPLEX_STEP_MOVE_FLOW, complexStepMoveFlowSaga);
    yield takeLatest(RecipeActionType.CONCURRENT_STEP_SET_MUST_COMPETE_FLOW, concurrentStepFlowSetMustCompeteSaga);
    yield takeLatest(RecipeActionType.CONDITIONAL_STEP_UPDATE_FLOW, conditionalStepUpdateFlowSaga);
    yield takeLatest(RecipeActionType.LOOP_STEP_UPDATE_CONDITION, loopStepUpdateConditionSaga);
    yield takeLatest(RecipeActionType.ALTERNATIVE_STEP_CHANGE_DEFAULT_FLOW, alternativeStepChangeDefaultFlowSaga);
    yield takeLatest(RecipeActionType.UPDATE_ALTERNATIVE_STEP, updateAlternativeStepSaga);
    yield takeLatest(RecipeActionType.REMOVE_ALTERNATIVE_STEP, removeAlternativeStepSaga);
    yield takeLatest(RecipeActionType.UPDATE_STEP, updateStepSaga);
    yield takeLatest(RecipeActionType.REMOVE_STEPS, removeRecipeStepsSaga);
    yield takeEvery(RecipeActionType.SET_RECIPE_DIALOG_STATES, setRecipeDialogStatesSaga);
    yield takeLatest(RecipeActionType.GET_CUSTOM_VARIABLE_DEFAULT_NAME, getCustomVariableDefaultNameSaga);
    yield takeLatest(RecipeActionType.GET_CUSTOM_VARIABLE_FORMULA_STATUS, getCustomVariableFormulaStatusSaga);
    yield takeLatest(RecipeActionType.ADD_CUSTOM_VARIABLE, addCustomVariableSaga);
    yield takeLatest(RecipeActionType.EDIT_CUSTOM_VARIABLE, updateCustomVariableSaga);
    yield takeLatest(RecipeActionType.DELETE_CUSTOM_VARIABLE, deleteCustomVariableSaga);
    yield takeLatest(RecipeActionType.LOAD_ALTERNATIVE_STEP_NAMES, loadAlternativeStepNamesSaga);
    yield takeLatest(RecipeActionType.LOAD_GAS_NAMES, loadGasNamesSaga);
    yield takeLatest(RecipeActionType.LOAD_LIQUID_NAMES, loadLiquidNamesSaga);
    yield takeLatest(RecipeActionType.LOAD_PARAMETER_NAMES, loadParameterNamesSaga);
    yield takeLatest(RecipeActionType.UPSERT_GAS, upsertGasSaga);
    yield takeLatest(RecipeActionType.DELETE_GAS, deleteGasSaga);
    yield takeLatest(RecipeActionType.UPSERT_LIQUID, upsertLiquidSaga);
    yield takeLatest(RecipeActionType.DELETE_LIQUID, deleteLiquidSaga);
    yield takeLatest(RecipeActionType.UPSERT_PARAMETER, upsertParameterSaga);
    yield takeLatest(RecipeActionType.DELETE_PARAMETER, deleteParameterSaga);
}
